/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

// Functions to get, set, and remove storage related to the user.
export const getUserAccessToken = (): string => sessionStorage.getItem('userAccessToken');
export const getUserId = (): string => sessionStorage.getItem('userId');
export const getUserFirstName = (): string => sessionStorage.getItem('userFirstName');
export const getUserLastName = (): string => sessionStorage.getItem('userLastName');
export const getUserCompany = (): string => sessionStorage.getItem('userCompany');
export const getExternalUser = (): boolean => sessionStorage.getItem('externalUser') === 'true';
export const getServicerUser = (): boolean => sessionStorage.getItem('servicerUser') === 'true';
export const getSuperUser = (): boolean => sessionStorage.getItem('superUser') === 'true';
export const getLoanNumber = (): string => sessionStorage.getItem('loanNumber');
export const getEmail = (): string => sessionStorage.getItem('email');
export const getBorrowerNumber = (): string => sessionStorage.getItem('borrowerNumber');
export const getLoggedOut = (): boolean => sessionStorage.getItem('loggedOut') === 'true';

export const removeUserAccessToken = (): void => sessionStorage.removeItem('userAccessToken');
export const removeUserId = (): void => sessionStorage.removeItem('userId');
export const removeUserFirstName = (): void => sessionStorage.removeItem('userFirstName');
export const removeUserLastName = (): void => sessionStorage.removeItem('userLastName');
export const removeUserCompany = (): void => sessionStorage.removeItem('userCompany');
export const removeExternalUser = (): void => sessionStorage.removeItem('externalUser');
export const removeServicerUser = (): void => sessionStorage.removeItem('servicerUser');
export const removeSuperUser = (): void => sessionStorage.removeItem('superUser');
export const removeLoanNumber = (): void => sessionStorage.removeItem('loanNumber');
export const removeEmail = (): void => sessionStorage.removeItem('email');
export const removeBorrowerNumber = (): void => sessionStorage.removeItem('borrowerNumber');
export const removeLoggedOut = (): void => sessionStorage.removeItem('loggedOut');

export const setUserAccessToken = (value: string): void => sessionStorage.setItem('userAccessToken', value);
export const setUserId = (value: string): void => sessionStorage.setItem('userId', value);
export const setUserFirstName = (value: string): void => sessionStorage.setItem('userFirstName', value);
export const setUserLastName = (value: string): void => sessionStorage.setItem('userLastName', value);
export const setUserCompany = (value: string): void => sessionStorage.setItem('userCompany', value);
export const setExternalUser = (value: boolean): void => sessionStorage.setItem('externalUser', value.toString());
export const setServicerUser = (value: boolean): void => sessionStorage.setItem('servicerUser', value.toString());
export const setSuperUser = (value: boolean): void => sessionStorage.setItem('superUser', value.toString());
export const setLoanNumber = (value: string): void => sessionStorage.setItem('loanNumber', value);
export const setEmail = (value: string): void => sessionStorage.setItem('email', value);
export const setBorrowerNumber = (value: string): void => sessionStorage.setItem('borrowerNumber', value);
export const setLoggedOut = (value: boolean): void => sessionStorage.setItem('loggedOut', value.toString());
export const setUserEmail = (value: string): void => sessionStorage.setItem('userEmail', value);
export const setUserType = (value: string): void => sessionStorage.setItem('userType', value);
export const setSessionToken = (value: string): void => sessionStorage.setItem('sessionToken', value);
export const removeUserEmail = (): void => sessionStorage.removeItem('userEmail');
export const removeUserType = (): void => sessionStorage.removeItem('userType');
export const removeSessionToken = (): void => sessionStorage.removeItem('sessionToken');
export const setAngelAISessionData = (userEmail: string, userType: string, sessionToken: string): void => {
  setUserEmail(userEmail);
  setUserType(userType);
  setSessionToken(sessionToken);
};

export const clearUserCookies = (): void =>
  document.cookie.split(';').forEach(function (c) {
    document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
  });

// remove the user storage
export const removeUserSession = (): void => {
  removeUserId();
  removeUserAccessToken();
  removeUserFirstName();
  removeUserLastName();
  removeUserCompany();
  removeExternalUser();
  removeServicerUser();
  removeSuperUser();
  removeLoanNumber();
  removeEmail();
  removeBorrowerNumber();
  removeLoggedOut();
  removeUserEmail();
  removeUserType();
  removeSessionToken();
  clearUserCookies();
  localStorage.removeItem('key');
};

// set the user storage and their session info
export const setUserSession = (token: string, id: string) => {
  // If its blank we have no reason to be setting it
  if (token !== undefined && id !== undefined) {
    setUserAccessToken(token);
    setUserId(id);
  }
};

/**
 * Retrieves all key-value pairs from local storage and session storage.
 *
 * @returns {Object} An object containing the key-value pairs from local storage and session storage.
 *
 * This function iterates through all the keys in local storage and session storage, collects
 * the key-value pairs, and returns them in a structured object with separate properties for
 * `localStorage` and `sessionStorage`.
 */
export const getUserStorage = () => {
  // Initialize an object to store local and session storage key-value pairs
  const storage = {
    localStorage: {},
    sessionStorage: {},
  };

  // Iterate through all keys in local storage
  for (let i = 0; i < localStorage.length; i++) {
    // Get the key at index i
    const key = localStorage.key(i);
    if (key !== null) {
      // Retrieve the value associated with the key and store it in the object
      storage.localStorage[key] = localStorage.getItem(key);
    }
  }

  // Iterate through all keys in session storage
  for (let i = 0; i < sessionStorage.length; i++) {
    // Get the key at index i
    const key = sessionStorage.key(i);
    if (key !== null) {
      // Retrieve the value associated with the key and store it in the object
      storage.sessionStorage[key] = sessionStorage.getItem(key);
    }
  }

  // Return the object containing all key-value pairs from both storages
  return storage;
};
